export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly detail?: unknown,
  ) {
    super(message)
  }
}

function getCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'))
  return m ? decodeURIComponent(m[1]) : null
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const method = (init.method || 'GET').toUpperCase()
  const headers = new Headers(init.headers)
  headers.set('Accept', 'application/json')

  if (!(init.body instanceof FormData) && init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const csrf = getCookie('zeus_csrf')
    if (csrf) headers.set('X-CSRF-Token', csrf)
  }

  const res = await fetch(path, { ...init, credentials: 'include', headers })

  const isJson = res.headers.get('content-type')?.includes('application/json')
  const body = isJson ? await res.json().catch(() => null) : null

  if (!res.ok) {
    throw new ApiError(
      (body as { detail?: string } | null)?.detail ?? res.statusText,
      res.status,
      body,
    )
  }

  return body as T
}

/** Best-effort human-readable message from a thrown API error.
 *
 * FastAPI returns `detail` as a plain string for HTTPException but as an ARRAY
 * of objects for a 422 validation error. Passing that array straight to a
 * toast makes React try to render objects as children and throws
 * "Objects are not valid as a React child", taking the page down instead of
 * showing the error.
 */
export function errorText(e: unknown, fallback: string): string {
  if (!(e instanceof ApiError)) return fallback
  const detail = (e.detail as { detail?: unknown } | null)?.detail
  if (typeof detail === 'string' && detail) return detail
  if (Array.isArray(detail)) {
    const parts = detail
      .map((d) => {
        if (typeof d === 'string') return d
        const msg = (d as { msg?: unknown })?.msg
        const loc = (d as { loc?: unknown })?.loc
        const where = Array.isArray(loc) ? loc.filter((s) => s !== 'body').join('.') : ''
        return typeof msg === 'string' ? (where ? `${where}: ${msg}` : msg) : null
      })
      .filter((s): s is string => Boolean(s))
    if (parts.length) return parts.join('; ')
  }
  return e.message || fallback
}
